import * as React from "react"
import "./click-away-listener.css"

export const ClickAwayListener = ({
    children,
    className,
    portalId,
    onClickAway,
    ...rest
}: React.HTMLAttributes<HTMLDivElement> & {
    onClickAway: (event: MouseEvent) => void
    portalId?: string
}) => {
    const ref = React.useRef<HTMLDivElement | null>(null)
    const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

    const clickAwayHandler = React.useCallback(
        (e: MouseEvent) => {
            // If another button handled this click...
            if (e.defaultPrevented) return

            const element = portalId ? document.getElementById(portalId) : null
            if (
                !ref.current?.contains(e.target as Node) &&
                document.body.contains(e.target as Node) &&
                (!element || !element.contains(e.target as Node))
            ) {
                onClickAway(e)
            }
        },
        [onClickAway, portalId]
    )

    React.useEffect(() => {
        timeoutRef.current = setTimeout(
            () => document.body.addEventListener("click", clickAwayHandler),
            0
        )
        document.body.addEventListener("contextmenu", clickAwayHandler)

        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current)
            document.body.removeEventListener("click", clickAwayHandler)
            document.body.removeEventListener("contextmenu", clickAwayHandler)
        }
    }, [clickAwayHandler])

    return (
        <div ref={ref} className={className} data-testid="click-away-listener" {...rest}>
            {children}
        </div>
    )
}

export const ClickAwayListenerV2 = ({
    children,
    className,
    portalId,
    onClickAway,
    ...rest
}: React.HTMLAttributes<HTMLDivElement> & {
    onClickAway: (event: MouseEvent) => void
    portalId?: string
}) => {
    const containerRef = React.useRef<HTMLDivElement>(null)

    React.useEffect(() => {
        const handleClickAway = (event: MouseEvent) => {
            const target = event.target as Node

            // Check if the click is outside our container
            if (containerRef.current && !containerRef.current.contains(target)) {
                // If portalId is specified, also check if the click is within that portal
                if (portalId) {
                    const portal = document.getElementById(portalId)
                    if (portal && portal.contains(target)) {
                        return // Don't trigger onClickAway if click is within the portal
                    }
                }

                onClickAway(event)
            }
        }

        // Add event listener to document
        document.addEventListener("mousedown", handleClickAway)

        // Cleanup function to remove event listener
        return () => {
            document.removeEventListener("mousedown", handleClickAway)
        }
    }, [onClickAway, portalId])

    return (
        <div ref={containerRef} className={className} {...rest}>
            {children}
        </div>
    )
}
